// src/pages/AffiliateSales.tsx
import React, { useEffect, useState } from "react";
import Header from "components/Header";
import Footer from "components/Footer";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// フロントからは相対パスで /api を叩く（本番／ローカル共通）
const API = process.env.REACT_APP_API_BASE_URL || "";


/* ===== JST 日付ヘルパー ===== */
function toJST(d: Date) {
  const tz = d.getTimezoneOffset(); // 分
  return new Date(d.getTime() + (tz + -540) * 60 * 1000); // -540分 = JST
}
function fmtYMD(d: Date) {
  const j = toJST(d);
  const y = j.getFullYear();
  const m = String(j.getMonth() + 1).padStart(2, "0");
  const da = String(j.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}
function startOfMonthJST(d = new Date()) {
  const j = toJST(d);
  return new Date(j.getFullYear(), j.getMonth(), 1);
}
function todayJST() {
  return toJST(new Date());
}

type Summary = { totalJPY: number; count: number };
type DayRow = { date: string; totalJPY: number; count: number };
type AffiliateRow = { affiliateName: string; totalJPY: number; count: number };

export default function AffiliateSales() {
  // チーム（この鍵）全体のサマリー
  const [summary, setSummary] = useState<Summary>({
    totalJPY: 0,
    count: 0,
  });

  // チーム合計の日別推移
  const [byDay, setByDay] = useState<DayRow[]>([]);

  // アカウント毎の集計（この鍵に紐づく各アカウント）
  const [byAffiliate, setByAffiliate] = useState<AffiliateRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 期間指定
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  // Affiliate.tsx で保存したグループキー（＝チームキー）
  const affKey =
    typeof window !== "undefined" ? localStorage.getItem("affKey") || "" : "";

  // 売上が無い／エラーのとき用のフォールバック：
  // verify-key からアカウント名だけ取って売上0円で並べる
  const fallbackLoadAccountsOnly = async () => {
    if (!affKey) {
      throw new Error("先にアフィリエイター画面で鍵認証を行ってください。");
    }

    const vr = await fetch(`${API}/api/affiliate/verify-key`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: affKey }),
    });

    if (!vr.ok) {
      throw new Error("データ取得に失敗しました");
    }

    const v = await vr.json().catch(() => null as any);
    const affs: any[] = Array.isArray(v?.affiliates) ? v.affiliates : [];

    const zeroList: AffiliateRow[] =
      affs.map((a) => ({
        // ★ 修正ポイント①（accountName 優先 → name → affiliateName）
        affiliateName: a.accountName || a.name || a.affiliateName || "",
        totalJPY: 0,
        count: 0,
      })) ?? [];

    setSummary({ totalJPY: 0, count: 0 });
    setByDay([]);
    setByAffiliate(zeroList);
    setErr(null);
  };

  const fetchSales = async (override?: { from?: string; to?: string }) => {
    // ▼ 鍵が無ければAPIを叩かずエラーメッセージ
    if (!affKey) {
      setErr("先にアフィリエイター画面で鍵認証を行ってください。");
      return;
    }

    const fromParam = override?.from ?? from;
    const toParam = override?.to ?? to;

    setLoading(true);
    setErr(null);
    try {
      const qs = new URLSearchParams();
      if (fromParam) qs.set("from", fromParam);
      if (toParam) qs.set("to", toParam);

      const r = await fetch(
        `${API}/api/affiliate/sales${
          qs.toString() ? `?${qs.toString()}` : ""
        }`,
        {
          headers: { "x-aff-key": affKey },
        }
      );

      if (!r.ok) {
        // APIエラー時：アカウントだけ0円で表示
        await fallbackLoadAccountsOnly();
        return;
      }

      const d: any = await r.json().catch(() => null);
      if (!d || d.ok === false) {
        await fallbackLoadAccountsOnly();
        return;
      }

      // ===== サマリー正規化（summary or group.summary） =====
      const rawSummary =
        d.summary || d.group?.summary || { totalJPY: 0, count: 0 };

      const summaryNorm: Summary = {
        totalJPY: Number(
          rawSummary.totalJPY ??
            rawSummary.totalAmount ??
            rawSummary.total ??
            0
        ),
        count: Number(
          rawSummary.count ?? rawSummary.totalOrders ?? rawSummary.orders ?? 0
        ),
      };

      // ===== 日別データ =====
      const rawByDay: any[] =
        (Array.isArray(d.group?.byDay) && d.group.byDay) ||
        (Array.isArray(d.byDay) && d.byDay) ||
        [];

      const byDayNorm: DayRow[] = rawByDay.map((row) => ({
        date: String(row.date ?? row.day ?? ""),
        totalJPY: Number(row.totalJPY ?? row.totalAmount ?? 0),
        count: Number(row.count ?? row.totalOrders ?? 0),
      }));

       // ===== アカウント毎 =====
      const rawAffiliates: any[] =
        (Array.isArray(d.group?.byAffiliate) && d.group.byAffiliate) ||
        (Array.isArray(d.byAffiliate) && d.byAffiliate) ||
        (Array.isArray(d.affiliates) && d.affiliates) ||
        [];

      const affiliatesNorm: AffiliateRow[] = rawAffiliates.map((a) => ({
        // accountName 優先 → name → affiliateName
        affiliateName: a.accountName || a.name || a.affiliateName || "",
        totalJPY: Number(
          a.totalJPY ?? a.totalAmount ?? a.amount ?? a.total ?? 0
        ),
        count: Number(a.count ?? a.totalOrders ?? a.orders ?? 0),
      }));

      // ===== データ有無 =====
      const hasData =
        affiliatesNorm.some(
          (a) => (a.totalJPY ?? 0) > 0 || (a.count ?? 0) > 0
        ) ||
        (byDayNorm ?? []).some(
          (d) => (d.totalJPY ?? 0) > 0 || (d.count ?? 0) > 0
        ) ||
        (summaryNorm.totalJPY ?? 0) > 0 ||
        (summaryNorm.count ?? 0) > 0;

      if (!hasData) {
        // 売上データが一切無いときは、アカウントだけ0円で表示
        await fallbackLoadAccountsOnly();
        return;
      }

      // ===== チーム全員をマージ（売上0のメンバーも表示） =====
      let mergedAffiliates: AffiliateRow[] = affiliatesNorm;

      try {
        const vr = await fetch(`${API}/api/affiliate/verify-key`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key: affKey }),
        });

        if (vr.ok) {
          const v = await vr.json().catch(() => null as any);
          const affs: any[] = Array.isArray(v?.affiliates) ? v.affiliates : [];

          // チーム所属アカウントを 0 円で初期化
          const zeroBase: AffiliateRow[] = affs.map((a) => ({
            affiliateName: a.accountName || a.name || a.affiliateName || "",
            totalJPY: 0,
            count: 0,
          }));

          const map = new Map<string, AffiliateRow>();

          // まず全員 0 円で入れる
          zeroBase.forEach((a) => {
            if (!a.affiliateName) return;
            map.set(a.affiliateName, { ...a });
          });

          // 売上データを上書きマージ
          affiliatesNorm.forEach((a) => {
            const key = a.affiliateName || "";
            if (!key) return;
            const base = map.get(key);
            if (base) {
              base.totalJPY = a.totalJPY;
              base.count = a.count;
            } else {
              map.set(key, { ...a });
            }
          });

          // 金額の大きい順＋名前順でソート
          mergedAffiliates = Array.from(map.values()).sort((a, b) => {
            const diff = (b.totalJPY || 0) - (a.totalJPY || 0);
            if (diff !== 0) return diff;
            return (a.affiliateName || "").localeCompare(
              b.affiliateName || "",
              "ja"
            );
          });
        }
      } catch {
        // verify-key が失敗しても、売上がある分だけは表示する
        mergedAffiliates = affiliatesNorm;
      }

      // 通常パターン：サマリー＋日別＋アカウント一覧をセット
      setSummary(summaryNorm);
      setByDay(byDayNorm);
      setByAffiliate(mergedAffiliates);
      setErr(null);
    } catch (e: any) {
      setErr(e?.message || "通信エラー");
      setSummary({ totalJPY: 0, count: 0 });
      setByDay([]);
      setByAffiliate([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (affKey) {
      fetchSales();
    } else {
      setErr("先にアフィリエイター画面で鍵認証を行ってください。");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== JSX ===== */
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f5f7fb",
      }}
    >
      <Header />
      <main
        style={{
          flex: 1,
          maxWidth: 720,
          margin: "0 auto",
          padding: "14px 10px 28px",
        }}
      >
        <h2
          style={{
            fontSize: 22,
            fontWeight: 900,
            marginBottom: 12,
          }}
        >
          売上確認（アカウント別＆チーム合計）
        </h2>

        {/* 期間指定コントロール（クイックボタン削除） */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={dateInput}
          />
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={dateInput}
          />

          <button
            type="button"
            style={btnPrimary}
            onClick={() => fetchSales()}
            disabled={loading}
          >
            {loading ? "確認中…" : "確認"}
          </button>
        </div>

        {loading && (
          <div style={{ color: "#1e73be", fontWeight: 700 }}>通信中…</div>
        )}
        {err && (
          <div
            style={{
              color: "#c0392b",
              fontWeight: 700,
              marginBottom: 8,
            }}
          >
            ⚠ {err}
          </div>
        )}

        {/* チーム全体のサマリー */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#0b1a27",
            }}
          >
            チーム全体の売上合計
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 900,
              color: "#1976d2",
            }}
          >
            ¥{summary.totalJPY.toLocaleString()}
          </div>
          <div
            style={{
              color: "#64748b",
              marginTop: 6,
              fontSize: 14,
            }}
          >
            購入件数：{summary.count}件
          </div>
          <div
            style={{
              color: "#94a3b8",
              marginTop: 4,
              fontSize: 12,
            }}
          >
            ※ この鍵に紐づく全アカウント（チーム）の合計です
          </div>
        </div>

        {/* 日別推移グラフ（チーム合計） */}
        {byDay.length > 0 && (
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "#0b1a27",
                marginBottom: 12,
              }}
            >
              日別売上推移（チーム合計）
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="totalJPY" name="売上金額" />
                <Bar dataKey="count" name="件数" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* アカウント毎の売上 */}
        {byAffiliate.length > 0 && (
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "#0b1a27",
                marginBottom: 10,
              }}
            >
              アカウント毎の売上（チーム内）
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#64748b",
                marginBottom: 8,
              }}
            >
              アフィリエイター画面で登録した各アカウント単位の売上です
            </div>

            <div>
              {byAffiliate.map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 4px",
                    borderTop: i === 0 ? "1px solid #eef2f7" : undefined,
                    borderBottom: "1px solid #eef2f7",
                  }}
                >
                  {/* 左：アカウント名＋件数 */}
                  <div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {a.affiliateName}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#64748b",
                        marginTop: 2,
                      }}
                    >
                      購入件数：{a.count}件
                    </div>
                  </div>

                  {/* 右：合計金額 */}
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: "#1976d2",
                      textAlign: "right",
                      minWidth: 120,
                    }}
                  >
                    ¥{a.totalJPY?.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA戻る */}
        <button
          style={{
            width: "100%",
            padding: "16px",
            borderRadius: 10,
            border: "none",
            background: "#1e73be",
            color: "#fff",
            fontSize: 18,
            fontWeight: 800,
            cursor: "pointer",
          }}
          onClick={() => (window.location.href = "/affiliate")}
        >
          ← アフィリエイター画面に戻る
        </button>
      </main>
      <Footer />
    </div>
  );
}

/* ===== スタイル ===== */
const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 13,
  color: "#334155",
};

const td: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: 14,
  color: "#0f172a",
};

const dateInput: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontSize: 13,
};

const btnRange: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};

const btnPrimary: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 10,
  background: "#1976d2",
  color: "#fff",
  fontWeight: 800,
  fontSize: 13,
  border: 0,
  cursor: "pointer",
};
